import { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from 'date-fns';
import {
    TbGitCommit,
    TbGitPullRequest,
    TbGitMerge,
    TbGitPullRequestClosed,
    TbGitPullRequestDraft,
    TbSearch,
    TbLoader2
} from 'react-icons/tb';
import { cn } from "@/lib/utils";
import { useGitHubSearch, useTaskGitHub } from '@/hooks/useTaskGitHub';
import { useDebounce } from '@/hooks/useDebounce';
import { useToast } from "@/components/ui/use-toast";

export default function LinkGitHubDialog({ open, onOpenChange, taskKey, organizationSlug, onLinked, onRefreshActivities }) {
    const [activeTab, setActiveTab] = useState('commits');
    const [searchQuery, setSearchQuery] = useState('');
    const [commits, setCommits] = useState([]);
    const [pullRequests, setPullRequests] = useState([]);
    const [linking, setLinking] = useState(null);
    const [error, setError] = useState(null);

    const { toast } = useToast();
    const debouncedQuery = useDebounce(searchQuery, 300);
    const { searchCommits, searchPullRequests, isSearching } = useGitHubSearch(organizationSlug, taskKey);
    const { linkCommit, linkPR } = useTaskGitHub(null, taskKey, organizationSlug);

    // Search when query changes
    useEffect(() => {
        if (!open) return;

        const search = async () => {
            setError(null);
            if (activeTab === 'commits') {
                const { results, error: searchError } = await searchCommits(debouncedQuery);
                setCommits(results);
                if (searchError) setError(searchError);
            } else {
                const { results, error: searchError } = await searchPullRequests(debouncedQuery);
                setPullRequests(results);
                if (searchError) setError(searchError);
            }
        };

        search();
    }, [debouncedQuery, activeTab, open, searchCommits, searchPullRequests]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setSearchQuery('');
            setCommits([]);
            setPullRequests([]);
            setError(null);
        }
    }, [open]);

    const handleLink = useCallback(async (type, item) => {
        const id = type === 'commit' ? item.sha : item.number;
        setLinking(`${type}-${id}`);

        try {
            let result;
            if (type === 'commit') {
                result = await linkCommit(item.sha, item.repositoryFullName);
            } else {
                result = await linkPR(item.number, item.repositoryFullName);
            }

            if (result.success) {
                onOpenChange(false);
                onLinked?.();
                if (onRefreshActivities) {
                    await onRefreshActivities();
                }
            } else {
                toast({
                    description: result.error,
                    variant: "destructive"
                });
            }
        } catch (error) {
            toast({
                description: error.message,
                variant: "destructive"
            });
        } finally {
            setLinking(null);
        }
    }, [linkCommit, linkPR, taskKey, toast, onLinked, onOpenChange, onRefreshActivities]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-base">Link to {taskKey}</DialogTitle>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-2 h-9">
                        <TabsTrigger value="commits" className="text-xs gap-1.5">
                            <TbGitCommit className="w-3.5 h-3.5" />
                            Commits
                        </TabsTrigger>
                        <TabsTrigger value="prs" className="text-xs gap-1.5">
                            <TbGitPullRequest className="w-3.5 h-3.5" />
                            Pull Requests
                        </TabsTrigger>
                    </TabsList>

                    <div className="relative mt-3">
                        <TbSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                            placeholder={activeTab === 'commits' ? "Search commits..." : "Search pull requests..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 h-9 text-sm"
                        />
                        {isSearching && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <TbLoader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />
                            </div>
                        )}
                    </div>

                    <TabsContent value="commits" className="mt-3">
                        <ScrollArea className="h-[280px]">
                            {error ? (
                                <div className="text-center py-8">
                                    <p className="text-destructive text-xs">{error}</p>
                                </div>
                            ) : commits.length === 0 ? (
                                <div className="text-center text-xs text-muted-foreground py-8">
                                    {isSearching ? 'Searching...' : 'No commits found'}
                                </div>
                            ) : (
                                <div className="space-y-0.5">
                                    {commits.map(commit => (
                                        <CommitSearchItem
                                            key={commit.sha}
                                            commit={commit}
                                            onSelect={() => handleLink('commit', commit)}
                                            isLinking={linking === `commit-${commit.sha}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </ScrollArea>
                    </TabsContent>

                    <TabsContent value="prs" className="mt-3">
                        <ScrollArea className="h-[280px]">
                            {error ? (
                                <div className="text-center py-8">
                                    <p className="text-destructive text-xs">{error}</p>
                                </div>
                            ) : pullRequests.length === 0 ? (
                                <div className="text-center text-xs text-muted-foreground py-8">
                                    {isSearching ? 'Searching...' : 'No pull requests found'}
                                </div>
                            ) : (
                                <div className="space-y-0.5">
                                    {pullRequests.map(pr => (
                                        <PRSearchItem
                                            key={`${pr.repositoryFullName}-${pr.number}`}
                                            pr={pr}
                                            onSelect={() => handleLink('pull_request', pr)}
                                            isLinking={linking === `pull_request-${pr.number}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </ScrollArea>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}

function CommitSearchItem({ commit, onSelect, isLinking }) {
    const { shortSha, message, author, timestamp, hasLinks } = commit;
    const firstLine = message?.split('\n')[0] || '';

    return (
        <button
            onClick={onSelect}
            disabled={isLinking || hasLinks}
            className={cn(
                "w-full grid grid-cols-[auto_1fr_auto] items-center gap-2.5 px-2 py-2 rounded-md text-left transition-colors",
                hasLinks
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:bg-muted/50"
            )}
        >
            <TbGitCommit className="w-4 h-4 text-muted-foreground" />
            <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">
                    <span className="font-mono text-foreground">{shortSha}</span>
                    {' '}{firstLine}
                </p>
                <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                    {author?.username || author?.name} · {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
                    {hasLinks && ' · Already linked'}
                </p>
            </div>
            {isLinking ? (
                <TbLoader2 className="w-4 h-4 text-muted-foreground animate-spin" />
            ) : (
                <div className="w-4" />
            )}
        </button>
    );
}

function PRSearchItem({ pr, onSelect, isLinking }) {
    const { number, title, state, isDraft, author, createdAt, hasLinks } = pr;

    let StateIcon;
    let stateColor;

    if (state === 'merged') {
        StateIcon = TbGitMerge;
        stateColor = 'text-purple-500';
    } else if (state === 'closed') {
        StateIcon = TbGitPullRequestClosed;
        stateColor = 'text-red-500';
    } else if (isDraft) {
        StateIcon = TbGitPullRequestDraft;
        stateColor = 'text-muted-foreground';
    } else {
        StateIcon = TbGitPullRequest;
        stateColor = 'text-green-500';
    }

    return (
        <button
            onClick={onSelect}
            disabled={isLinking || hasLinks}
            className={cn(
                "w-full grid grid-cols-[auto_1fr_auto] items-center gap-2.5 px-2 py-2 rounded-md text-left transition-colors",
                hasLinks
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:bg-muted/50"
            )}
        >
            <StateIcon className={cn("w-4 h-4", stateColor)} />
            <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">
                    <span className="text-foreground">#{number}</span>
                    {' '}{title}
                </p>
                <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                    {author?.login} · {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
                    {hasLinks && ' · Already linked'}
                </p>
            </div>
            {isLinking ? (
                <TbLoader2 className="w-4 h-4 text-muted-foreground animate-spin" />
            ) : (
                <div className="w-4" />
            )}
        </button>
    );
}
